import { ChangeEvent, useState, useEffect } from 'react'
import { DataTableProps } from 'ts/interfaces/Components'
import useFetch from 'hooks/useFetch'
import toast from 'react-hot-toast';
import useModalStore from 'store/modal'
import { Card, Error as ErrorMessage, ReceiptSearchInput, Table } from 'components/ui'
import { useTrader } from 'context/TraderTypeContext';

const DataTable = <T extends object>({
  dataKey,
  url,
  columns,
  config,
  listName
}: DataTableProps<T>) => {
  const [searchUrl, setSearchUrl] = useState(url)
  const { isLoading, error, tableInstance, refresh } = useFetch(
    dataKey,
    searchUrl,
    columns,
    config,
    listName
  )

  const openModal = useModalStore((state) => state.openModal)
  const { traderId } = useTrader();
  const getLocalDateString = () => {
    const date = new Date();
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };
  
  const [startDate, setStartDate] = useState(getLocalDateString());
  const [endDate, setEndDate] = useState(getLocalDateString());
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedOption, setSelectedOption] = useState('transactionNo');
  
  const fetchReceipts = async () => {
    const formattedStartDate = startDate ? new Date(startDate).toISOString() : '';
    const formattedEndDate = endDate ? new Date(endDate + 'T23:59:59.999Z').toISOString() : '';
    const newUrl = `${url}&fromDate=${formattedStartDate}&toDate=${formattedEndDate}&${selectedOption}=${searchTerm}`;
    setSearchUrl(newUrl)
    await refresh()
  };

  useEffect(() => {
    fetchReceipts();
  }, [traderId])

  const resetState = () => {
    const dateString = getLocalDateString();
    setStartDate(dateString);
    setEndDate(dateString);
    setSearchTerm('');
    setSelectedOption('transactionNo');
    fetchReceipts();
  };
  
  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.currentTarget.value)
  }

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    try {
      const formattedStartDate = startDate ? new Date(startDate).toISOString() : '';
      const formattedEndDate = endDate ? new Date(endDate + 'T23:59:59.999Z').toISOString() : '';
      const newUrl = `${url}&fromDate=${formattedStartDate}&toDate=${formattedEndDate}&${selectedOption}=${searchTerm}`;
      console.log("DATE:", new Date().toISOString().split('T')[0]);
      setSearchUrl(newUrl);
    } catch (err) {
      const errorMessage = (err as Error).message || 'An unexpected error occurred';
      toast.error(errorMessage);
    }
  };

  return (
    <div className="p-4 flex justify-center">
      {error ? (
        <div className="flex">
          <ErrorMessage data={error} />
        </div>
      ) : null}

      <Card className="w-max px-4 py-3">
        <form onSubmit={handleSubmit} className="flex space-x-2 max-w-4xl justify-between">
          <div className="flex flex-col justify-end text-xs">
            <label htmlFor="startDate" className="opacity-60">From:</label>
            <input
              type="date"
              id="startDate"
              className="p-2 border rounded w-28"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </div>
          <div className="flex flex-col justify-end text-xs">
            <label htmlFor="endDate" className="opacity-60">To:</label>
            <input
              type="date"
              id="endDate"
              className="p-2 border rounded w-28"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>
          <div className="flex flex-col">
            <label className="text-xs opacity-60 ml-0.5" htmlFor="search">Search:</label>
            <ReceiptSearchInput
              placeholder="Search"
              value={searchTerm}
              onChange={handleInputChange}
              className="bg-white focus:shadow flex-1 rounded border ml-0.5 px-3 text-xs outline-none w-48 text-gray-800"
            />
          </div>
          <div className="flex flex-col">
            <label className="text-xs opacity-60 ml-0.5" htmlFor="searchBy">Filter by:</label>
            <select 
              className="p-2 border rounded text-sm" 
              value={selectedOption} 
              onChange={(e) => setSelectedOption(e.target.value)}
            >
              <option value="transactionNo">Transaction No.</option>
              <option value="traceNo">Trace No.</option>
              <option value="operatorId">Operator ID</option>
            </select>
          </div>
          <div className="flex items-end space-x-1">
            <button type="submit" disabled={isLoading} className={`px-4 py-2 ${isLoading ? "bg-gray-400" : "bg-indigo-500 hover:bg-green-500"} text-white rounded text-sm`}>
              Search
            </button>
            <button onClick={resetState} className="px-4 py-2 bg-gray-600 hover:bg-green-500 text-white rounded text-sm">
              Reset
            </button>
          </div>
        </form>
        <Table
          tableInstance={tableInstance}
          isLoading={isLoading}
          onRowClick={openModal}
        />
      </Card>
    </div>
  )
}

DataTable.defaultProps = {
  config: {}
}

export default DataTable
